import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Message, useToaster } from "rsuite";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import {
  SectionRibbon,
  Button,
  FormActions,
  Input,
  Picker,
  GlassCard,
  SpacedGlassCard,
} from "../../../../../ui";

export const GenerateConfigEDU = ({ config, regex_models }) => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const [formValueConfig, setFormValueConfig] = useState({
    system_id: config?.system_id || id,
    file_name: config?.file_name ?? null,
    regex_models: config?.regex_models ?? [],
    pg_tables: config?.pg_tables ?? [],
    schedule: config?.schedule ?? null,
  });
  const [loading, setLoading] = useState(false);
  const [regexModelsSelectData, setRegexModelsSelectData] = useState([]);
  const [initConfig, setInitConfig] = useState({
    system_id: config?.system_id || id,
    file_name: config?.file_name ?? null,
    regex_models: config?.regex_models ?? [],
    pg_tables: config?.pg_tables ?? [],
    schedule: config?.schedule ?? null,
  });

  // PREFILL DATA - Use useMemo to recompute when config changes
  const fileNameOptions = React.useMemo(() => {
    if (config) {
      return [...new Set([config?.file_name, "v2_edu", "v2_edu2"])]
        .filter(Boolean)
        .map((item) => ({ label: item, value: item }));
    } else {
      return ["v2_edu", "v2_edu2"].map((item) => ({
        label: item,
        value: item,
      }));
    }
  }, [config]);

  const pgTableOptions = React.useMemo(() => {
    if (config) {
      return [
        ...new Set(
          regex_models
            ?.filter((model) => model.pg_table?.includes("edu"))
            .map((table) => table.pg_table)
            .concat(config?.pg_tables || []),
        ),
      ].map((item) => ({ label: item, value: item }));
    } else {
      return [
        ...new Set(
          regex_models
            ?.filter((model) => model.pg_table?.includes("edu"))
            .map((table) => table.pg_table) || [],
        ),
      ].map((item) => ({ label: item, value: item }));
    }
  }, [config, regex_models]);

  // Update form when config prop changes (e.g., on page refresh)
  useEffect(() => {
    if (config) {
      console.log("EDU config received:", config);
      const updatedConfig = {
        system_id: config?.system_id || id,
        file_name: config?.file_name ?? null,
        regex_models: config?.regex_models ?? [],
        pg_tables: config?.pg_tables ?? [],
        schedule: config?.schedule ?? null,
      };
      console.log("Updated EDU config:", updatedConfig);
      setFormValueConfig(updatedConfig);
      setInitConfig(updatedConfig);
    }
  }, [config, id]);

  const resetForm = () => {
    setFormValueConfig(initConfig);
  };

  const handleInputChange = (field, value) => {
    // Handle both direct values and event objects
    const actualValue = value?.target ? value.target.value : value;
    setFormValueConfig((prev) => ({
      ...prev,
      [field]: actualValue,
    }));
  };

  const updateRegexModels = () => {
    if (!regex_models) return;

    if (formValueConfig.file_name?.includes("edu2")) {
      if (formValueConfig.regex_models?.length > 0) {
        setRegexModelsSelectData(
          [
            ...new Set(
              regex_models
                .filter((model) => model.pg_table?.includes("edu.v2"))
                .map((config) => config.id)
                .concat(formValueConfig.regex_models),
            ),
          ].map((item) => ({
            label: item,
            value: item,
          })),
        );
      } else {
        setRegexModelsSelectData(
          [
            ...new Set(
              regex_models
                .filter((model) => model.pg_table?.includes("edu.v2"))
                .map((config) => config.id),
            ),
          ].map((item) => ({
            label: item,
            value: item,
          })),
        );
      }
    } else {
      if (formValueConfig.regex_models?.length > 0) {
        setRegexModelsSelectData(
          [
            ...new Set(
              regex_models
                .filter((model) => model.pg_table?.includes("edu.v1"))
                .map((config) => config.id)
                .concat(formValueConfig.regex_models),
            ),
          ].map((item) => ({
            label: item,
            value: item,
          })),
        );
      } else {
        setRegexModelsSelectData(
          [
            ...new Set(
              regex_models
                .filter((model) => model.pg_table?.includes("edu.v1"))
                .map((config) => config.id),
            ),
          ].map((item) => ({
            label: item,
            value: item,
          })),
        );
      }
    }
  };

  const updateConfigEDU = async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/config-edu`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          config: formValueConfig,
          type: "edu",
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          ENV configuration updated successfully.
        </Message>,
        "topCenter",
      );
    } catch (error) {
      console.error("Failed to update EDU config:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update ENV configuration. Please try again.
        </Message>,
        "topCenter",
      );
    }

    setLoading(false);
  };

  const hasChanges = Object.keys(formValueConfig).some(
    (key) => formValueConfig[key] !== initConfig[key],
  );

  useEffect(() => {
    updateRegexModels();
  }, [regex_models]);

  useEffect(() => {
    updateRegexModels();
  }, [formValueConfig.file_name]);

  return (
    <EDUContainer>
      <SectionRibbon title="ENV Configuration" variant="primary" />

      <ConfigurationForm>
        <SpacedGlassCard>
          <FormContent>
            <SectionHeader>
              <SectionTitle>ENV Data Configuration</SectionTitle>
            </SectionHeader>

            <FormGrid>
              <FormField span="full">
                <Input
                  label="System ID"
                  value={formValueConfig.system_id}
                  onChange={(value) => handleInputChange("system_id", value)}
                  disabled
                  placeholder="System ID"
                />
              </FormField>

              <FormField span="double">
                <Picker
                  data={fileNameOptions}
                  value={formValueConfig.file_name}
                  onChange={(value) => handleInputChange("file_name", value)}
                  placeholder="Select file name"
                  searchable={false}
                />
              </FormField>

              <FormField span="double">
                <Picker
                  data={pgTableOptions}
                  value={formValueConfig.pg_tables}
                  onChange={(value) => handleInputChange("pg_tables", value)}
                  placeholder="Select PostgreSQL tables"
                  multiple
                  searchable={false}
                />
              </FormField>

              <FormField>
                <Input
                  label="Schedule"
                  value={formValueConfig.schedule}
                  onChange={(value) => handleInputChange("schedule", value)}
                  placeholder="Enter schedule"
                  type="number"
                  min={0}
                />
              </FormField>

              <FormField span="full">
                <Picker
                  data={regexModelsSelectData}
                  value={formValueConfig.regex_models}
                  onChange={(value) => handleInputChange("regex_models", value)}
                  placeholder="Select regex models"
                  multiple
                  searchable={false}
                />
              </FormField>
            </FormGrid>

            <FormButtonActions>
              <Button variant="subtle" onClick={resetForm} disabled={loading}>
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={updateConfigEDU}
                loading={loading}
              >
                Submit
              </Button>
            </FormButtonActions>
          </FormContent>
        </SpacedGlassCard>
      </ConfigurationForm>

      <ConfigurationInfo>
        <GlassCard>
          <InfoContent>
            <InfoHeader>
              <InfoTitle>ENV Configuration Guidelines</InfoTitle>
            </InfoHeader>

            <InfoGrid>
              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>File Configuration</InfoItemTitle>
                  <InfoItemText>
                    Select the appropriate file name pattern for EDU data
                    extraction. Common patterns include v2_edu and v2_edu2.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Database Tables</InfoItemTitle>
                  <InfoItemText>
                    Choose PostgreSQL tables that will store the processed EDU
                    data. Multiple tables can be selected for comprehensive data
                    storage.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Schedule Format</InfoItemTitle>
                  <InfoItemText>
                    Use cron format for scheduling (e.g., "0 0 * * *" for daily
                    at midnight). Leave empty for manual processing only.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>
            </InfoGrid>
          </InfoContent>
        </GlassCard>
      </ConfigurationInfo>
    </EDUContainer>
  );
};

// Styled Components
const EDUContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ConfigurationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormContent = styled.div`
  padding: 2rem;
`;

const FormButtonActions = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  gap: 1rem;
  display: flex;
  justify-content: flex-end;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ span }) => {
    if (span === "full") return "grid-column: 1 / -1;";
    if (span === "double") return "grid-column: span 2;";
    return "";
  }}

  @media (max-width: 1200px) {
    ${({ span }) => {
      if (span === "double") return "grid-column: span 2;";
      return "grid-column: span 1;";
    }}
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ConfigurationInfo = styled.div`
  margin-top: 1rem;
`;

const InfoContent = styled.div`
  padding: 2rem;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const InfoItemIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoItemContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoItemText = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.5;
`;
